import React, { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription
} from '@/components/ui/form'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Loader2, Save } from 'lucide-react'
import { OrganizationSettingUpdate } from '@/app/types/organizationSettings'
import { listAccounts } from '@/app/services/accountService'
import { listTaxRates } from '@/app/services/taxRateService'
import { SimpleAccountSelector } from '@/app/components/common/SimpleAccountSelector'
import { useAuthStore } from '@/app/store/authStore'
import type { AuthState } from '@/app/store/authStore'
import type { User } from '@/app/types/auth'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { FormControl } from '@/components/ui/form'

// Define selectors
const selectUser = (state: AuthState): User | null => state.user

const formSchema = z.object({
  default_sales_account_id: z.number().optional().nullable(),
  default_purchase_account_id: z.number().optional().nullable(),
  default_debtors_account_id: z.number().optional().nullable(),
  default_creditors_account_id: z.number().optional().nullable(),
  default_rounding_account_id: z.number().optional().nullable(),
  default_cgst_output_account_id: z.number().optional().nullable(),
  default_sgst_output_account_id: z.number().optional().nullable(),
  default_igst_output_account_id: z.number().optional().nullable(),
  default_cgst_input_account_id: z.number().optional().nullable(),
  default_sgst_input_account_id: z.number().optional().nullable(),
  default_igst_input_account_id: z.number().optional().nullable(),
  default_intra_state_tax_rate_id: z.number().optional().nullable(),
  default_inter_state_tax_rate_id: z.number().optional().nullable(),
  default_exempt_tax_rate_id: z.number().optional().nullable(),
  default_expense_account_id: z.number().optional().nullable(),
  default_depreciation_account_id: z.number().optional().nullable(),
  default_accumulated_depreciation_account_id: z.number().optional().nullable(),
  gl_posting_trigger_status: z.enum(['Approved', 'Paid', 'Posted']).optional().nullable(),
  default_razorpay_clearing_account_id: z.number().optional().nullable()
})

type OrganizationSettingsFormValues = z.infer<typeof formSchema>

interface OrganizationSettingsFormProps {
  onSubmit: (data: OrganizationSettingUpdate) => void
  defaultValues?: Partial<OrganizationSettingsFormValues>
  isLoading?: boolean
}

const OrganizationSettingsForm: React.FC<OrganizationSettingsFormProps> = ({
  onSubmit,
  defaultValues,
  isLoading = false
}) => {
  const user = useAuthStore(selectUser)
  const userOrgId = useMemo(() => user?.organization_id || user?.organization?.id, [user])

  // Test with queries
  // Fetch accounts
  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['accounts', userOrgId],
    queryFn: async () => {
      if (!userOrgId) return { items: [] }
      return listAccounts({ page: 1, page_size: 1000 }, userOrgId)
    },
    enabled: !!userOrgId
  })

  // Fetch tax rates
  const { data: taxRatesData, isLoading: taxRatesLoading } = useQuery({
    queryKey: ['taxRates', userOrgId],
    queryFn: async () => {
      if (!userOrgId) return { items: [] }
      return listTaxRates({ page: 1, page_size: 100 }, userOrgId)
    },
    enabled: !!userOrgId
  })

  const accounts = accountsData?.items || []
  const taxRates = taxRatesData?.items || []
  const isLoadingData = accountsLoading || taxRatesLoading

  const form = useForm<OrganizationSettingsFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      default_sales_account_id: defaultValues?.default_sales_account_id ?? null,
      default_purchase_account_id: defaultValues?.default_purchase_account_id ?? null,
      default_debtors_account_id: defaultValues?.default_debtors_account_id ?? null,
      default_creditors_account_id: defaultValues?.default_creditors_account_id ?? null,
      default_rounding_account_id: defaultValues?.default_rounding_account_id ?? null,
      default_cgst_output_account_id: defaultValues?.default_cgst_output_account_id ?? null,
      default_sgst_output_account_id: defaultValues?.default_sgst_output_account_id ?? null,
      default_igst_output_account_id: defaultValues?.default_igst_output_account_id ?? null,
      default_cgst_input_account_id: defaultValues?.default_cgst_input_account_id ?? null,
      default_sgst_input_account_id: defaultValues?.default_sgst_input_account_id ?? null,
      default_igst_input_account_id: defaultValues?.default_igst_input_account_id ?? null,
      default_intra_state_tax_rate_id: defaultValues?.default_intra_state_tax_rate_id ?? null,
      default_inter_state_tax_rate_id: defaultValues?.default_inter_state_tax_rate_id ?? null,
      default_exempt_tax_rate_id: defaultValues?.default_exempt_tax_rate_id ?? null,
      default_expense_account_id: defaultValues?.default_expense_account_id ?? null,
      default_depreciation_account_id: defaultValues?.default_depreciation_account_id ?? null,
      default_accumulated_depreciation_account_id:
        defaultValues?.default_accumulated_depreciation_account_id ?? null,
      gl_posting_trigger_status: defaultValues?.gl_posting_trigger_status ?? null,
      default_razorpay_clearing_account_id:
        defaultValues?.default_razorpay_clearing_account_id ?? null
    }
  })

  // Reset form when defaultValues change
  useEffect(() => {
    if (defaultValues) {
      form.reset({
        default_sales_account_id: defaultValues.default_sales_account_id ?? null,
        default_purchase_account_id: defaultValues.default_purchase_account_id ?? null,
        default_debtors_account_id: defaultValues.default_debtors_account_id ?? null,
        default_creditors_account_id: defaultValues.default_creditors_account_id ?? null,
        default_rounding_account_id: defaultValues.default_rounding_account_id ?? null,
        default_cgst_output_account_id: defaultValues.default_cgst_output_account_id ?? null,
        default_sgst_output_account_id: defaultValues.default_sgst_output_account_id ?? null,
        default_igst_output_account_id: defaultValues.default_igst_output_account_id ?? null,
        default_cgst_input_account_id: defaultValues.default_cgst_input_account_id ?? null,
        default_sgst_input_account_id: defaultValues.default_sgst_input_account_id ?? null,
        default_igst_input_account_id: defaultValues.default_igst_input_account_id ?? null,
        default_intra_state_tax_rate_id: defaultValues.default_intra_state_tax_rate_id ?? null,
        default_inter_state_tax_rate_id: defaultValues.default_inter_state_tax_rate_id ?? null,
        default_exempt_tax_rate_id: defaultValues.default_exempt_tax_rate_id ?? null,
        default_expense_account_id: defaultValues.default_expense_account_id ?? null,
        default_depreciation_account_id: defaultValues.default_depreciation_account_id ?? null,
        default_accumulated_depreciation_account_id:
          defaultValues.default_accumulated_depreciation_account_id ?? null,
        gl_posting_trigger_status: defaultValues.gl_posting_trigger_status ?? null,
        default_razorpay_clearing_account_id:
          defaultValues.default_razorpay_clearing_account_id ?? null
      })
    }
  }, [defaultValues, form])

  const handleSubmit = async (values: OrganizationSettingsFormValues) => {
    // Filter out null values and prepare the update payload
    const updateData: OrganizationSettingUpdate = {}

    Object.entries(values).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        updateData[key as keyof OrganizationSettingUpdate] = value as any
      }
    })

    onSubmit(updateData)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Default Accounts Section */}
        <Card>
          <CardHeader>
            <CardTitle>Default Accounts</CardTitle>
            <CardDescription>
              Configure the default accounts used for various transactions.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="default_sales_account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sales Account</FormLabel>
                  <FormDescription>Default account for sales transactions</FormDescription>
                  <SimpleAccountSelector
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select sales account"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="default_purchase_account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Purchase Account</FormLabel>
                  <FormDescription>Default account for purchase transactions</FormDescription>
                  <SimpleAccountSelector
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select purchase account"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="default_debtors_account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sundry Debtors Account</FormLabel>
                  <FormDescription>Control account for customers</FormDescription>
                  <SimpleAccountSelector
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select debtors account"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="default_creditors_account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sundry Creditors Account</FormLabel>
                  <FormDescription>Control account for suppliers</FormDescription>
                  <SimpleAccountSelector
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select creditors account"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="default_expense_account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Expense Account</FormLabel>
                  <FormDescription>Default account for miscellaneous expenses</FormDescription>
                  <SimpleAccountSelector
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select expense account"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="default_rounding_account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rounding Account</FormLabel>
                  <FormDescription>Account for rounding differences</FormDescription>
                  <SimpleAccountSelector
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select rounding account"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="default_depreciation_account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Depreciation Account</FormLabel>
                  <FormDescription>Account for depreciation expenses</FormDescription>
                  <SimpleAccountSelector
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select depreciation account"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="default_accumulated_depreciation_account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Accumulated Depreciation Account</FormLabel>
                  <FormDescription>Account for accumulated depreciation</FormDescription>
                  <SimpleAccountSelector
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select accumulated depreciation account"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="default_razorpay_clearing_account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Razorpay Clearing Account</FormLabel>
                  <FormDescription>Account for Razorpay payment clearing</FormDescription>
                  <SimpleAccountSelector
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select Razorpay clearing account"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Tax Accounts Section */}
        <Card>
          <CardHeader>
            <CardTitle>Tax Accounts</CardTitle>
            <CardDescription>Configure the default accounts for tax calculations.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h4 className="text-sm font-medium mb-4">Output Tax Accounts (Sales)</h4>
              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="default_cgst_output_account_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CGST Output Account</FormLabel>
                      <SimpleAccountSelector
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select CGST output"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="default_sgst_output_account_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>SGST Output Account</FormLabel>
                      <SimpleAccountSelector
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select SGST output"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="default_igst_output_account_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>IGST Output Account</FormLabel>
                      <SimpleAccountSelector
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select IGST output"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            <div>
              <h4 className="text-sm font-medium mb-4">Input Tax Accounts (Purchases)</h4>
              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="default_cgst_input_account_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CGST Input Account</FormLabel>
                      <SimpleAccountSelector
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select CGST input"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="default_sgst_input_account_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>SGST Input Account</FormLabel>
                      <SimpleAccountSelector
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select SGST input"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="default_igst_input_account_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>IGST Input Account</FormLabel>
                      <SimpleAccountSelector
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select IGST input"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tax Rate Settings Section */}
        <Card>
          <CardHeader>
            <CardTitle>Default Tax Rates</CardTitle>
            <CardDescription>
              Configure the default tax rates for different transaction types.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-3">
            <FormField
              control={form.control}
              name="default_intra_state_tax_rate_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Intra-State Tax Rate</FormLabel>
                  <FormDescription>
                    Default tax rate for sales within the same state
                  </FormDescription>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value !== 'none' ? parseInt(value) : null)
                    }
                    value={field.value?.toString() || 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select intra-state tax rate" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {taxRates.map((taxRate) => (
                        <SelectItem key={taxRate.id} value={taxRate.id.toString()}>
                          {taxRate.name} ({taxRate.rate}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="default_inter_state_tax_rate_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Inter-State Tax Rate</FormLabel>
                  <FormDescription>Default tax rate for sales to other states</FormDescription>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value !== 'none' ? parseInt(value) : null)
                    }
                    value={field.value?.toString() || 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select inter-state tax rate" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {taxRates.map((taxRate) => (
                        <SelectItem key={taxRate.id} value={taxRate.id.toString()}>
                          {taxRate.name} ({taxRate.rate}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="default_exempt_tax_rate_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Exempt Tax Rate</FormLabel>
                  <FormDescription>Default tax rate for exempt transactions</FormDescription>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value !== 'none' ? parseInt(value) : null)
                    }
                    value={field.value?.toString() || 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select exempt tax rate" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {taxRates.map((taxRate) => (
                        <SelectItem key={taxRate.id} value={taxRate.id.toString()}>
                          {taxRate.name} ({taxRate.rate}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* General Settings Section */}
        <Card>
          <CardHeader>
            <CardTitle>General Settings</CardTitle>
            <CardDescription>Configure general behavior settings.</CardDescription>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="gl_posting_trigger_status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>GL Posting Trigger Status</FormLabel>
                  <FormDescription>
                    When should transactions be posted to General Ledger
                  </FormDescription>
                  <Select
                    onValueChange={(value) => field.onChange(value !== 'none' ? value : null)}
                    value={field.value || 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select GL posting trigger" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="Approved">Approved</SelectItem>
                      <SelectItem value="Paid">Paid</SelectItem>
                      <SelectItem value="Posted">Posted</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={isLoading || isLoadingData}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}

export default OrganizationSettingsForm
